// MODO EMERGENCIA ([DA-845], 24/10/2026, pedido do Ruan): com a VPS fora do ar, as MESMAS telas do ERP seguem
// abrindo (o `public/sw-sistema.js` serve a copia da pagina), com uma tarja no cabecalho e so quatro
// telas liberadas — Orcamento novo, Consulta de produtos, Orcamentos e Clientes. O resto fica
// desabilitado. Este arquivo e' a unica fonte de "estamos em emergencia?".
//
// DOIS estados ([DA-849], 26/10/2026, pedido do Ruan): o sistema DETECTA a falta de servidor e so
// mostra a tarja "Sem conexao" com o botao "Modo Emergencia"; quem LIGA o modo e' o usuario, no
// botao. Ate aqui a deteccao ligava o modo sozinha e, no tablet do Joao, um Wi-Fi instavel
// paralisava a tela (toda chamada parava de sair). Sem o clique, nada e' bloqueado.
//
// Como detecta: qualquer chamada que falha por REDE ou 502/503/504 (nginx sem a API) dispara uma
// sondagem do `/api/version` com tempo-limite curto; tres sondagens sem resposta = sem conexao. Uma
// sondagem periodica cobre quem esta parado numa tela (sem ela, a queda so seria notada no proximo
// clique, depois do tempo-limite do navegador — o site nao passa pela Cloudflare, a conexao fica
// pendurada em vez de responder erro).
// Como sai: a primeira resposta do servidor apaga a tarja "Sem conexao" sozinha; o MODO ligado so
// sai no botao "Voltar ao normal" (decisao dele, 26/10: um soluco de segundos faria o modo sair e
// voltar na tela de quem atende). Os orcamentos EMG sobem nesse clique.
import { useSyncExternalStore } from 'react';

const TEMPO_LIMITE_SONDA_MS = 4000;
const INTERVALO_NORMAL_MS = 30_000;
const INTERVALO_EMERGENCIA_MS = 15_000;

let semConexao = false;
let ativo = false;
const ouvintes = new Set<() => void>();
let sondando: Promise<boolean> | null = null;

function avisar(): void {
  for (const f of ouvintes) f();
}

/** Modo emergencia LIGADO pelo usuario: as chamadas nem saem, so as quatro telas funcionam. */
export function emEmergencia(): boolean {
  return ativo;
}

/** O servidor nao esta respondendo (com ou sem o modo ligado). */
export function estaSemConexao(): boolean {
  return semConexao;
}

function definir(sc: boolean, at: boolean): void {
  if (semConexao === sc && ativo === at) return;
  semConexao = sc;
  ativo = at;
  document.documentElement.toggleAttribute('data-emergencia', at);
  avisar();
}

function marcarSemConexao(): void {
  definir(true, ativo);
}

/** O servidor respondeu: apaga "Sem conexao". O modo, se ligado, fica ate o botao. */
export function servidorRespondeu(): void {
  definir(false, ativo);
}

/** Botao "Modo Emergencia" da tarja. So liga com o servidor sem responder. */
export function ativarModoEmergencia(): void {
  if (semConexao) definir(true, true);
}

/** Botao "Voltar ao normal" da tarja. So sai com o servidor respondendo. */
export function voltarAoNormal(): void {
  if (!semConexao) definir(false, false);
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

export function useEmergencia(): boolean {
  return useSyncExternalStore(assinar, () => ativo);
}

export function useSemConexao(): boolean {
  return useSyncExternalStore(assinar, () => semConexao);
}

async function servidorResponde(): Promise<boolean> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TEMPO_LIMITE_SONDA_MS);
  try {
    const r = await fetch('/api/version', { cache: 'no-store', signal: ctrl.signal });
    // Qualquer resposta da API (mesmo 4xx) prova que ela esta viva; 5xx do nginx nao.
    return r.status < 500;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Confere o servidor e liga/desliga a tarja "Sem conexao". Pra LIGAR exige TENTATIVAS_QUEDA falhas
 * seguidas, espacadas (~20s no total): o restart de um deploy deixa a API alguns segundos sem
 * responder, e isso nao e' queda. Pra DESLIGAR basta uma resposta. Chamadas simultaneas dividem a
 * mesma sondagem.
 */
const TENTATIVAS_QUEDA = 3;
const ESPERA_ENTRE_TENTATIVAS_MS = 3000;
export function sondarServidor(): Promise<boolean> {
  if (!sondando) {
    sondando = (async () => {
      for (let i = 0; i < TENTATIVAS_QUEDA; i++) {
        if (i > 0) await new Promise((r) => setTimeout(r, ESPERA_ENTRE_TENTATIVAS_MS));
        if (await servidorResponde()) { servidorRespondeu(); return true; }
        // Ja sem conexao, uma falha basta pra continuar assim.
        if (semConexao) return false;
      }
      marcarSemConexao();
      return false;
    })().finally(() => { sondando = null; });
  }
  return sondando;
}

/** Chamado pela camada da API quando uma chamada falha por rede ou 502/503/504. */
export function suspeitarQueda(): void {
  if (!semConexao) void sondarServidor();
}

export function ehFalhaDeRede(e: unknown): boolean {
  return e instanceof TypeError || (e instanceof DOMException && e.name === 'AbortError');
}

let relogio: ReturnType<typeof setTimeout> | null = null;
function agendar(): void {
  if (relogio) clearTimeout(relogio);
  relogio = setTimeout(async () => {
    await sondarServidor();
    agendar();
  }, semConexao || ativo ? INTERVALO_EMERGENCIA_MS : INTERVALO_NORMAL_MS);
}

/** Liga a sondagem periodica e o service worker. Chamado uma vez, no main.tsx. */
export function iniciarModoEmergencia(): void {
  agendar();
  ouvintes.add(agendar);
  if (import.meta.env.PROD && 'serviceWorker' in navigator && !window.location.pathname.startsWith('/app')) {
    navigator.serviceWorker.register('/sw-sistema.js', { scope: '/' }).catch((e) => {
      console.error('[emergencia] service worker nao registrou:', e);
    });
  }
}

// Telas que funcionam na emergencia. Comparacao EXATA: '/produtos' libera a lista, nao a ficha
// '/produtos/:id'; '/orcamentos' libera a lista, e '/orcamentos/novo' o orcamento novo.
export const TELAS_EMERGENCIA: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/orcamentos/novo', label: 'Orcamento novo' },
  { to: '/produtos', label: 'Consulta de produtos' },
  { to: '/orcamentos', label: 'Orcamentos' },
  { to: '/cadastros/clientes', label: 'Clientes' },
];

export function telaLiberadaNaEmergencia(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '') || '/';
  // Orcamento FEITO na emergencia (id negativo, so existe neste aparelho) reabre pela lista.
  if (/^\/orcamentos\/-\d+$/.test(p)) return true;
  return TELAS_EMERGENCIA.some((t) => t.to === p);
}

// ---- usuario guardado: sem servidor nao ha /auth/me, e quem ja estava logado segue logado ----
const USUARIO_KEY = 'fiodecobre.usuario';

export function guardarUsuario(u: unknown | null): void {
  try {
    if (u) localStorage.setItem(USUARIO_KEY, JSON.stringify(u));
    else localStorage.removeItem(USUARIO_KEY);
  } catch { /* navegador sem armazenamento: so perde o modo emergencia */ }
}

export function lerUsuarioGuardado<T>(): T | null {
  try {
    const s = localStorage.getItem(USUARIO_KEY);
    return s ? (JSON.parse(s) as T) : null;
  } catch {
    return null;
  }
}

/** Acende a tarja direto, sem sondar — usado quando a abertura ja provou que o servidor nao respondeu. */
export function entrarSemConexao(): void {
  marcarSemConexao();
}
